export type GardenMode = "system" | "auto" | "manual";
export type GardenSeason = "spring" | "summer" | "autumn" | "winter";
export interface GardenSettings {
  mode: GardenMode;
  minutes: number;
  season: GardenSeason;
  manualVisited: boolean;
}

export const SEASONS: GardenSeason[] = ["spring", "summer", "autumn", "winter"];
export const SEASON_NAMES = {
  de: {
    spring: "Frühling",
    summer: "Sommer",
    autumn: "Herbst",
    winter: "Winter",
  },
  en: {
    spring: "Spring",
    summer: "Summer",
    autumn: "Autumn",
    winter: "Winter",
  },
};

export function calendarSeason(date: Date): GardenSeason {
  const month = date.getMonth();
  if (month >= 2 && month <= 4) return "spring";
  if (month >= 5 && month <= 7) return "summer";
  if (month >= 8 && month <= 10) return "autumn";
  return "winter";
}

// Local clock time, matching the original garden's 06:30–18:30 day cycle.
export function isGardenDay(minutes: number): boolean {
  const time = minutes % 1440;
  return time >= 390 && time < 1110;
}

export function gardenTheme(
  mode: GardenMode,
  minutes: number,
): "system" | "light" | "dark" {
  return mode === "system" ? "system" : isGardenDay(minutes) ? "light" : "dark";
}

export function formatGardenTime(minutes: number): string {
  const time = minutes % 1440;
  return `${String(Math.floor(time / 60)).padStart(2, "0")}:${String(time % 60).padStart(2, "0")}`;
}

export function readGardenSettings(raw: string | null): GardenSettings {
  const fallback: GardenSettings = {
    mode: "system",
    minutes: 720,
    season: "spring",
    manualVisited: false,
  };
  if (!raw) return fallback;
  try {
    const value = JSON.parse(raw);
    if (value?.version !== 1 && value?.version !== 2) return fallback;
    return {
      // Adopt System on migration; keep manual values ready for an explicit choice.
      mode:
        value.version === 1
          ? "system"
          : ["system", "auto", "manual"].includes(value.mode)
            ? value.mode
            : fallback.mode,
      minutes:
        Number.isInteger(value.minutes) &&
        value.minutes >= 0 &&
        value.minutes <= 1440
          ? value.minutes
          : 720,
      season: SEASONS.includes(value.season) ? value.season : "spring",
      manualVisited: value.manualVisited === true || value.mode === "manual",
    };
  } catch {
    return fallback;
  }
}

const SKY_STOPS = [
  [0, "#101934", "#293957"],
  [300, "#25264d", "#b4778b"],
  [420, "#a6cfe6", "#f5c9a6"],
  [720, "#b7dbed", "#edf3df"],
  [990, "#9bc9dc", "#f5d5a3"],
  [1140, "#45446b", "#da8d7e"],
  [1260, "#172140", "#394565"],
  [1440, "#101934", "#293957"],
] as const;

function mixColor(a: string, b: string, ratio: number): string {
  const channels = [1, 3, 5].map((offset) => {
    const start = parseInt(a.slice(offset, offset + 2), 16);
    const end = parseInt(b.slice(offset, offset + 2), 16);
    return Math.round(start + (end - start) * ratio);
  });
  return `rgb(${channels.join(", ")})`;
}

export function gardenPalette(minutes: number, season: GardenSeason) {
  const time = minutes % 1440;
  const index = SKY_STOPS.findIndex((stop) => stop[0] > time);
  const end = SKY_STOPS[index];
  const start = SKY_STOPS[index - 1];
  const ratio = (time - start[0]) / (end[0] - start[0]);
  const colors = {
    spring: {
      accent: "#9e83b8",
      foliage: "#94bca0",
      hill: "#718e80",
      glow: "#d8b9d6",
    },
    summer: {
      accent: "#b99c46",
      foliage: "#739968",
      hill: "#55786a",
      glow: "#e8ce87",
    },
    autumn: {
      accent: "#c08b64",
      foliage: "#c89773",
      hill: "#8d7769",
      glow: "#e6b086",
    },
    winter: {
      accent: "#809fb9",
      foliage: "#c7d9e3",
      hill: "#8b9faa",
      glow: "#b9d6e8",
    },
  }[season];
  return {
    ...colors,
    sky: mixColor(start[1], end[1], ratio),
    horizon: mixColor(start[2], end[2], ratio),
  };
}
